import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react'
import {
  BLOCKING_STATUSES,
  type Booking, type Database, type Guest, type Invoice, type Payment, type Quote,
} from './model'
import { buildDemoDatabase, DEMO_TOURIST_TAX } from './demoSeed'
import * as api from './supabaseAdapter'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth'
import { addDays, overlaps, todayISO } from '../lib/dates'
import { draftInvoices, lockingInvoices } from '../lib/bookingLock'
import {
  priceStay, prepaymentFor, prepaymentPolicyOf,
  type PriceResult, type TouristTaxRule,
} from '../lib/pricing'
import { docLineLabel } from '../locales/documents'

const DEMO_STORAGE_KEY = 'staysuite.db.v3'

/**
 * Der Anteil, den ein Betrieb ohne eigene Einstellung als Anzahlung verlangt.
 *
 * Bleibt als Rückfall bestehen — die tatsächliche Regel steht in den Stammdaten
 * (`prepayment_mode`), und `prepaymentPolicyOf()` liest sie dort.
 */
export const PREPAYMENT_PERCENT = 30

/**
 * Loads the demo database, merged over a freshly built one.
 *
 * A stored snapshot is always from an older version of the schema: the moment a new
 * collection is added, every browser holding the previous payload is missing that key
 * and the first `[...db.newThing]` throws. Bumping the storage key "fixes" it by
 * throwing the visitor's data away and only until the next field is added, so instead
 * the stored value fills in over the defaults — missing collections come back as the
 * seeded ones, and anything the visitor changed is kept.
 */
function loadDemo(): Database {
  const fresh = buildDemoDatabase()
  try {
    const raw = localStorage.getItem(DEMO_STORAGE_KEY)
    if (!raw) return fresh
    const stored = JSON.parse(raw) as Partial<Database>

    // Die Beispieldaten liegen relativ zum Tag ihrer Erzeugung: Anreise „heute",
    // Abreise „in drei Tagen". Ein gespeicherter Abzug von vorgestern zeigt deshalb
    // Anreisen in der Vergangenheit und einen leeren Kalender voraus — und wer die
    // Demo zum Verkaufen zeigt, merkt das im schlechtesten Moment. Also lieber die
    // Herumprobiererei des Besuchers verwerfen als eine tote Demo zeigen; der Hinweis
    // oben sagt ohnehin, dass Änderungen nur in diesem Browser bleiben.
    if (stored.seeded_on !== fresh.seeded_on) return fresh

    const merged = {
      ...fresh, ...stored, tenant: { ...fresh.tenant, ...stored.tenant },
    } as unknown as Record<string, unknown>
    // Any collection the stored payload predates stays as the freshly seeded one.
    for (const [key, value] of Object.entries(fresh)) {
      if (Array.isArray(value) && !Array.isArray(merged[key])) merged[key] = value
    }
    return merged as unknown as Database
  } catch {
    // Corrupt payload — a fresh demo database beats a white screen.
    return fresh
  }
}

export interface StoreValue {
  db: Database
  live: boolean
  loading: boolean
  error: string | null
  clearError: () => void
  touristTax: TouristTaxRule
  /**
   * Das gebuchte Paket und seine Grenze. `null` in der Demo — dort gibt es
   * keine Abonnementzeile, und das heißt „nicht blockieren", nicht „alles
   * sperren". Gelesen wird die Zeile ohnehin schon; bis 0060 wurde sie
   * weggeworfen.
   */
  subscription: api.Subscription | null
  unitById: (id: string) => Database['units'][number] | undefined
  guestById: (id: string | null) => Guest | undefined
  guestName: (id: string | null) => string
  bookingsInRange: (from: string, to: string) => Booking[]
  isAvailable: (unitId: string, from: string, to: string, exceptBookingId?: string) => boolean
  conflictsFor: (booking: Booking) => Booking[]
  /** Overlapping bookings of ANY status — for warning, not for blocking. */
  overlappingBookings: (unitId: string, from: string, to: string, exceptBookingId?: string) => Booking[]
  price: (b: Pick<Booking,
    'unit_id' | 'arrival' | 'departure' | 'adults' | 'children' | 'children_ages'>
    & Partial<Pick<Booking,
      'optional_fee_ids' | 'nightly_rate_override' | 'discount_amount' | 'discount_reason'>>
  ) => PriceResult | null
  /**
   * `false` = nicht gespeichert. Der Grund steht dann in `error` und wird angezeigt.
   * Aufrufer, die danach weiterarbeiten (Blatt schließen, Beleg erzeugen), müssen den
   * Wert prüfen — sonst führen sie eine Buchung fort, die es gar nicht gibt.
   */
  saveBooking: (b: Booking) => Promise<boolean>
  deleteBooking: (id: string) => Promise<void>
  saveGuest: (g: Guest) => Promise<Guest>
  /** Returns the new document's id so the caller can open it straight away. */
  createQuote: (bookingId: string, override?: Booking) => Promise<string | null>
  createInvoice: (bookingId: string) => Promise<string | null>
  /** Assigns the next number, freezes the content, makes the row immutable. */
  issueInvoice: (invoiceId: string) => Promise<void>
  /** Storno: issues a credit note against an invoice. Returns its id. */
  cancelInvoice: (invoiceId: string, reason: string) => Promise<string | null>
  /** A Stripe checkout on the LANDLORD's own account. Returns the link. */
  guestPaymentLink: (invoiceId: string) => Promise<string | null>
  addPayment: (p: Omit<Payment, 'id' | 'tenant_id' | 'created_at'>) => Promise<void>
  /** Nimmt eine irrtümlich gebuchte Zahlung wieder zurück, samt aller Folgen. */
  removePayment: (paymentId: string) => Promise<void>
  /** Issuer details that appear on every document (address, VAT id, bank account). */
  saveTenant: (patch: Partial<Database['tenant']>) => Promise<void>
  saveTouristTax: (rule: TouristTaxRule) => Promise<void>
  saveRatePeriod: (id: string, patch: Partial<Database['rate_periods'][number]>) => Promise<void>
  upsertRatePeriod: (row: Database['rate_periods'][number]) => Promise<void>
  deleteRatePeriod: (id: string) => Promise<void>
  saveFee: (row: Database['fees'][number]) => Promise<void>
  deleteFee: (id: string) => Promise<void>
  saveUnit: (row: Database['units'][number]) => Promise<void>
  deleteUnit: (id: string) => Promise<void>
  saveUnitCategory: (row: Database['unit_categories'][number]) => Promise<void>
  deleteUnitCategory: (id: string) => Promise<void>
  paidFor: (bookingId: string) => number
  /**
   * Ausgestellte, nicht stornierte Rechnungen dieser Buchung. Leer heißt: Anreise,
   * Abreise und Einheit sind änderbar. Die Regel steht in `lib/bookingLock.ts`.
   */
  invoiceLockFor: (bookingId: string) => Invoice[]
  /** Entwürfe zur Buchung — werden benannt, nie heimlich mitgeschrieben. */
  draftInvoicesFor: (bookingId: string) => Invoice[]
  reload: () => void
  reset: () => void
  nextReference: (kind: 'booking' | 'quote' | 'invoice') => string
}

const Ctx = createContext<StoreValue | null>(null)

/** Placeholder used while the live tenant is still loading. */
const EMPTY_DB: Database = {
  tenant: {
    id: '', slug: '', name: '', country: 'AT', currency: 'EUR', locale: 'de',
    timezone: 'Europe/Vienna', vat_scheme: 'standard',
    vat_rate_accommodation: 10, vat_rate_services: 20,
  },
  properties: [], unit_categories: [], units: [], rate_periods: [], fees: [], guests: [], bookings: [],
  booking_items: [], quotes: [], invoices: [], invoice_items: [], payments: [],
  ical_feeds: [], ical_events: [],
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const { mode, tenantId } = useAuth()
  const live = mode === 'live'

  const [db, setDb] = useState<Database>(() => (live ? EMPTY_DB : loadDemo()))
  const [touristTax, setTouristTax] = useState<TouristTaxRule>(DEMO_TOURIST_TAX)
  const [subscription, setSubscription] = useState<api.Subscription | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)
  const loadedTenant = useRef<string | null>(null)

  // Demo data is persisted locally so a prospect's clicking-around survives a reload.
  useEffect(() => {
    if (mode !== 'demo') return
    try {
      localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(db))
    } catch {
      // Quota exceeded in a private window: keep working in memory.
    }
  }, [db, mode])

  useEffect(() => {
    if (mode === 'demo' && loadedTenant.current !== 'demo') {
      loadedTenant.current = 'demo'
      setDb(loadDemo())
      setTouristTax(DEMO_TOURIST_TAX)
    }
  }, [mode])

  useEffect(() => {
    if (!live || !tenantId || !supabase) return
    let alive = true
    setLoading(true)
    api.loadDatabase(supabase, tenantId)
      .then((bundle) => {
        if (!alive) return
        loadedTenant.current = tenantId
        setDb(bundle.db)
        setSubscription(bundle.subscription)
        /*
         * Kein Ortstaxen-Satz hinterlegt heißt: es wird keine eingehoben.
         *
         * WARUM HIER NICHT DER DEMOSATZ DURCHSCHIMMERN DARF: bis zum 28.07.2026
         * stand hier `{ ...DEMO_TOURIST_TAX, amount_per_person_night: 0 }`. Der
         * Betrag war damit zwar null, aber ALLES ANDERE kam aus den Demodaten —
         * und so las ein Vermieter in Hamburg auf seiner Abrechnung
         * „Ortstaxe · Sölden". Beim ersten Durchlauf der ganzen Kette stand es
         * bei einem Betrieb, der nie eine Gemeinde eingetragen hatte.
         *
         * Die Ersatzregel trägt deshalb keinen Ort und keinen fremden Namen.
         */
        setTouristTax(bundle.touristTax ?? {
          id: 'none',
          municipality: '',
          name: { de: 'Ortstaxe', en: 'Tourist tax' },
          amount_per_person_night: 0,
          vat_rate: 0,
          exempt_under_age: null,
          reduced_under_age: null,
          reduced_amount: null,
          max_nights: null,
        })
        setError(null)
      })
      .catch((e: Error) => { if (alive) setError(e.message) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [live, tenantId, reloadToken])

  const unitById = useCallback(
    (id: string) => db.units.find((u) => u.id === id), [db.units])

  const guestById = useCallback(
    (id: string | null) => (id ? db.guests.find((g) => g.id === id) : undefined), [db.guests])

  const guestName = useCallback((id: string | null) => {
    const g = id ? db.guests.find((x) => x.id === id) : undefined
    if (!g) return '—'
    return [g.first_name, g.last_name].filter(Boolean).join(' ') || g.company || g.email || '—'
  }, [db.guests])

  const bookingsInRange = useCallback(
    (from: string, to: string) =>
      db.bookings.filter(
        (b) => b.status !== 'cancelled' && overlaps(b.arrival, b.departure, from, to),
      ),
    [db.bookings],
  )

  const isAvailable = useCallback(
    (unitId: string, from: string, to: string, exceptBookingId?: string) =>
      !db.bookings.some(
        (b) =>
          b.unit_id === unitId &&
          b.id !== exceptBookingId &&
          (b.kind === 'block' || BLOCKING_STATUSES.includes(b.status)) &&
          overlaps(b.arrival, b.departure, from, to),
      ),
    [db.bookings],
  )

  /**
   * Every non-cancelled booking that overlaps this range, whatever its status.
   *
   * `isAvailable` answers "may I book here" and deliberately ignores enquiries and
   * quotes, because two guests may ask about the same week. This answers "is anyone
   * already pencilled in here", which is what the landlord needs to be told even
   * when the answer does not stop them.
   */
  const overlappingBookings = useCallback(
    (unitId: string, from: string, to: string, exceptBookingId?: string) =>
      db.bookings.filter(
        (b) =>
          b.unit_id === unitId &&
          b.id !== exceptBookingId &&
          b.status !== 'cancelled' &&
          overlaps(b.arrival, b.departure, from, to),
      ),
    [db.bookings],
  )

  const conflictsFor = useCallback(
    (booking: Booking) =>
      db.ical_events
        .filter(
          (e) =>
            !e.dismissed && !e.booking_id &&
            e.unit_id === booking.unit_id &&
            overlaps(e.arrival, e.departure, booking.arrival, booking.departure),
        )
        .map((e) => ({ ...booking, id: e.id, arrival: e.arrival, departure: e.departure })),
    [db.ical_events],
  )

  const price = useCallback<StoreValue['price']>((b) => {
    const unit = db.units.find((u) => u.id === b.unit_id)
    if (!unit) return null
    return priceStay({
      unit,
      ratePeriods: db.rate_periods,
      fees: db.fees,
      touristTax,
      arrival: b.arrival,
      departure: b.departure,
      adults: b.adults,
      children: b.children,
      childrenAges: b.children_ages ?? [],
      optionalFeeIds: b.optional_fee_ids ?? [],
      // Was an dieser einen Buchung ausgehandelt wurde. Es geht durch die Rechnung
      // und nicht an ihr vorbei: nur so bleibt der Gesamtpreis richtig, wenn sich
      // später Zeitraum oder Belegung ändern.
      nightlyOverride: b.nightly_rate_override ?? null,
      discount: b.discount_amount ?? 0,
      discountReason: b.discount_reason ?? null,
      vatScheme: db.tenant.vat_scheme,
      vatRateAccommodation: db.tenant.vat_rate_accommodation,
      pricesIncludeVat: db.tenant.prices_include_vat ?? false,
      childRates: db.tenant.child_rates ?? [],
      childRateScope: db.tenant.child_rate_scope ?? 'surcharge',
    })
  }, [db.units, db.rate_periods, db.fees, db.tenant, touristTax])

  /** Local fallback numbering. In live mode the database issues the real number. */
  const nextReference = useCallback((kind: 'booking' | 'quote' | 'invoice') => {
    const year = todayISO().slice(0, 4)
    const prefix = { booking: 'B', quote: 'A', invoice: 'R' }[kind]
    const existing =
      kind === 'booking' ? db.bookings.map((b) => b.reference)
      : kind === 'quote' ? db.quotes.map((q) => q.number)
      : db.invoices.map((i) => i.number)
    const nums = existing
      .filter((r): r is string => !!r && r.startsWith(`${prefix}-${year}-`))
      .map((r) => Number(r.split('-')[2]) || 0)
    return `${prefix}-${year}-${String(Math.max(0, ...nums) + 1).padStart(4, '0')}`
  }, [db.bookings, db.quotes, db.invoices])

  const failed = (e: unknown) => {
    const msg = e instanceof Error ? e.message : String(e)
    setError(msg === 'OVERLAP' ? 'Zeitraum ist bereits belegt.' : msg)
  }

  const saveBooking = useCallback(async (b: Booking): Promise<boolean> => {
    /*
     * Ohne Gast ist es keine Buchung.
     *
     * Der Anlass: eine Buchung stand als „bestätigt" im Kalender, ohne dass irgendwo
     * ein Name hinterlegt war. Wer soll da anreisen? Ein Balken ohne Namen ist eine
     * Behauptung über belegte Nächte, die niemand einlösen kann — und er blockiert
     * eine Einheit, die in Wahrheit frei ist.
     *
     * Die Prüfung steht hier und nicht nur an der Maske: die Maske ist ein Weg von
     * mehreren (Verschieben, Statuswechsel in der Abrechnung), und eine Regel, die
     * jeder Weg selbst kennen muss, kennt irgendwann einer nicht.
     *
     * Sperren sind ausgenommen: eine Sperre ist eine Tatsache über das Objekt
     * (Handwerker, Eigennutzung) und wartet auf niemanden.
     */
    if (b.kind !== 'block' && !b.guest_id) {
      setError('Ohne hinterlegten Gast lässt sich die Buchung nicht speichern.')
      return false
    }

    // A locked price is the agreed price: recomputing it here would silently undo
    // the landlord's decision the next time anything about the booking is saved.
    const priced = (b.kind === 'block' || b.price_locked) ? null : price(b)
    const next: Booking = priced
      ? {
          ...b,
          tenant_id: db.tenant.id || b.tenant_id,
          total_net: priced.totals.net,
          total_vat: priced.totals.vat,
          total_gross: priced.totals.gross,
          prepayment_amount:
            b.prepayment_amount || prepaymentFor(priced.totals.gross, prepaymentPolicyOf(db.tenant)),
          prepayment_due: b.prepayment_due ?? addDays(todayISO(), 7),
        }
      : { ...b, tenant_id: db.tenant.id || b.tenant_id }

    const put = (row: Booking) => setDb((d) => ({
      ...d,
      bookings: d.bookings.some((x) => x.id === row.id)
        ? d.bookings.map((x) => (x.id === row.id ? row : x))
        : [...d.bookings, row],
    }))

    if (!live || !supabase) { put(next); return true }
    try {
      put(await api.upsertBooking(supabase, next))
      setError(null)
      return true
    } catch (e) {
      failed(e)
      setReloadToken((t) => t + 1)   // resync after a rejected write
      return false
    }
  }, [live, price, db.tenant.id])

  const deleteBooking = useCallback(async (id: string) => {
    const previous = db.bookings
    setDb((d) => ({ ...d, bookings: d.bookings.filter((b) => b.id !== id) }))
    if (!live || !supabase) return
    try {
      await api.deleteBooking(supabase, id)
    } catch (e) {
      failed(e)
      setDb((d) => ({ ...d, bookings: previous }))
    }
  }, [live, db.bookings])

  const saveGuest = useCallback(async (g: Guest) => {
    const row = { ...g, tenant_id: db.tenant.id || g.tenant_id }
    const put = (x: Guest) => setDb((d) => ({
      ...d,
      guests: d.guests.some((y) => y.id === x.id)
        ? d.guests.map((y) => (y.id === x.id ? x : y))
        : [...d.guests, x],
    }))
    if (!live || !supabase) { put(row); return row }
    try {
      const saved = await api.upsertGuest(supabase, row)
      put(saved)
      return saved
    } catch (e) { failed(e); return row }
  }, [live, db.tenant.id])

  /**
   * `override` ist für den Fall, dass die Buchung soeben geändert wurde: `db` in
   * dieser Closure ist der Stand vom letzten Rendern, und ein Angebot, das direkt
   * nach einer Verschiebung erzeugt wird, würde sonst die alten Daten anbieten.
   */
  const createQuote = useCallback(async (bookingId: string, override?: Booking) => {
    const booking = override ?? db.bookings.find((b) => b.id === bookingId)
    if (!booking) return null
    const priced = price(booking)
    if (!priced) return null
    const guest = db.guests.find((g) => g.id === booking.guest_id)
    // Document language follows the guest, not the landlord's UI.
    const docLang: 'de' | 'en' =
      (guest?.language ?? db.tenant.locale) === 'en' ? 'en' : 'de'

    const number = live && supabase
      ? await api.nextDocumentNumber(supabase, db.tenant.id, 'quote').catch(() => nextReference('quote'))
      : nextReference('quote')

    const quote: Quote = {
      id: crypto.randomUUID(),
      tenant_id: db.tenant.id,
      booking_id: bookingId,
      number,
      status: 'draft',
      language: guest?.language ?? db.tenant.locale,
      valid_until: addDays(todayISO(), 14),
      currency: db.tenant.currency,
      total_net: priced.totals.net,
      total_vat: priced.totals.vat,
      total_gross: priced.totals.gross,
      prepayment_amount: prepaymentFor(priced.totals.gross, prepaymentPolicyOf(db.tenant)),
      deposit_amount: booking.deposit_amount,
      // Den Wortlaut mitnehmen, nicht verknüpfen: ändert der Betrieb später seine
      // Bedingungen, darf sich ein bereits verschicktes Angebot nicht rückwirkend
      // ändern. Leer bleibt leer — dann gelten die gesetzlichen Bestimmungen, und
      // der Beleg sagt das auch so.
      terms_text: db.tenant.cancellation_terms?.trim() || undefined,
      public_token: '',
      // Freeze the priced lines. A quote the guest received must never silently
      // change because a seasonal rate was edited afterwards.
      snapshot: {
        lines: priced.lines.map((l) => ({
          label: l.nameJson?.[docLang] ?? l.nameJson?.de ?? docLineLabel(docLang, l.code),
          qty: l.qty,
          unitPrice: l.unitPrice,
          net: l.net,
          vatRate: l.vatRate,
          vat: l.vat,
          gross: l.gross,
        })),
      },
      created_at: new Date().toISOString(),
    }

    try {
      const saved = live && supabase ? await api.insertQuote(supabase, quote) : quote
      setDb((d) => ({
        ...d,
        quotes: [saved, ...d.quotes],
        bookings: d.bookings.map((b) =>
          b.id === bookingId && b.status === 'enquiry' ? { ...b, status: 'quoted' } : b),
      }))
      if (live && supabase && booking.status === 'enquiry') {
        /*
         * Eigener try: das Angebot ist an dieser Stelle bereits angelegt. Ein
         * Fehlschlag beim Umschalten der Buchung darf ihn nicht als „nicht erzeugt"
         * melden — sonst klickt der Vermieter erneut und hat zwei Angebote mit zwei
         * Nummern. Gemeldet wird trotzdem, und neu geladen: die Buchung steht dann
         * wieder auf „Anfrage", was der Wahrheit entspricht.
         */
        try {
          api.assertWrote(
            await supabase.from('bookings').update({ status: 'quoted' })
              .eq('id', bookingId).select('id'),
            'Buchung auf „Angebot" setzen',
          )
          setError(null)
        } catch (e) {
          failed(e)
          setReloadToken((t) => t + 1)
        }
        return saved.id
      }
      setError(null)
      return saved.id
    } catch (e) { failed(e); return null }
  }, [live, db, price, nextReference])

  const createInvoice = useCallback(async (bookingId: string) => {
    const booking = db.bookings.find((b) => b.id === bookingId)
    if (!booking) return null
    const priced = price(booking)
    if (!priced) return null
    const guest = db.guests.find((g) => g.id === booking.guest_id)
    const language = guest?.language ?? db.tenant.locale

    // No number yet. A draft is a proposal the guest may still argue with; the
    // number is taken when it is issued, because the sequence has to be gapless and
    // a deleted draft would otherwise leave a hole in it.
    const invoiceId = crypto.randomUUID()
    const invoice: Invoice = {
      id: invoiceId,
      tenant_id: db.tenant.id,
      booking_id: bookingId,
      guest_id: booking.guest_id,
      number: null,
      type: 'invoice',
      status: 'draft',
      language,
      issue_date: todayISO(),
      service_from: booking.arrival,
      service_to: booking.departure,
      due_date: addDays(todayISO(), 14),
      currency: db.tenant.currency,
      total_net: priced.totals.net,
      total_vat: priced.totals.vat,
      total_gross: priced.totals.gross,
      paid_amount: 0,
      public_token: '',
      created_at: new Date().toISOString(),
    }
    const items = priced.lines.map((l, i) => ({
      id: crypto.randomUUID(),
      invoice_id: invoiceId,
      kind: l.kind,
      // Der Code bleibt, das Label ist übersetzt und änderbar — Auswertungen
      // müssen sich auf den Code stützen können.
      code: l.code,
      label: l.nameJson?.[language] ?? l.nameJson?.de
        ?? docLineLabel(language === 'en' ? 'en' : 'de', l.code),
      qty: l.qty,
      unit_price: l.unitPrice,
      amount_net: l.net,
      vat_rate: l.vatRate,
      amount_vat: l.vat,
      amount_gross: l.gross,
      sort_order: i,
    }))

    try {
      const saved = live && supabase
        ? await api.insertInvoice(supabase, invoice, items)
        : { invoice, items }
      setDb((d) => ({
        ...d,
        invoices: [saved.invoice, ...d.invoices],
        invoice_items: [...d.invoice_items, ...saved.items],
      }))
      setError(null)
      return saved.invoice.id
    } catch (e) { failed(e); return null }
  }, [live, db, price, nextReference])

  /**
   * Turn a draft into a document: take the next number, freeze the content, and make
   * the row read-only. In live mode this is one database function, so a failure
   * cannot burn a number; in demo mode it is mirrored locally.
   */
  const issueInvoice = useCallback(async (invoiceId: string) => {
    const current = db.invoices.find((i) => i.id === invoiceId)
    if (!current || current.issued_at) return
    if (live && supabase) {
      try {
        const issued = await api.issueInvoice(supabase, invoiceId)
        setDb((d) => ({
          ...d,
          invoices: d.invoices.map((i) => (i.id === invoiceId ? issued : i)),
        }))
        setError(null)
      } catch (e) { failed(e) }
      return
    }
    const number = nextReference('invoice')
    setDb((d) => ({
      ...d,
      invoices: d.invoices.map((i) => (i.id === invoiceId
        ? { ...i, number, issued_at: new Date().toISOString(), status: 'sent' as const,
            issue_date: todayISO() }
        : i)),
    }))
  }, [live, db.invoices, nextReference])

  /**
   * Storno. An issued invoice cannot be deleted or edited, so undoing one means
   * issuing a document that cancels it out: same lines, negative amounts, its own
   * number, pointing back at what it corrects.
   */
  const cancelInvoice = useCallback(async (invoiceId: string, reason: string) => {
    const original = db.invoices.find((i) => i.id === invoiceId)
    if (!original || !original.issued_at) return null

    if (live && supabase) {
      try {
        const note = await api.cancelInvoice(supabase, invoiceId, reason)
        // Reload rather than patching by hand: the call also flips the original and
        // writes the credit note's items, and guessing at all three invites drift.
        setReloadToken((n) => n + 1)
        setError(null)
        return note.id
      } catch (e) { failed(e); return null }
    }

    const noteId = crypto.randomUUID()
    const items = db.invoice_items.filter((it) => it.invoice_id === invoiceId)
    const note: Invoice = {
      ...original,
      id: noteId,
      number: nextReference('invoice'),
      type: 'credit_note',
      status: 'sent',
      issue_date: todayISO(),
      issued_at: new Date().toISOString(),
      due_date: undefined,
      total_net: -original.total_net,
      total_vat: -original.total_vat,
      total_gross: -original.total_gross,
      paid_amount: 0,
      corrects_invoice_id: invoiceId,
      intro_text: reason || `Storno zu Rechnung ${original.number ?? ''}`,
      created_at: new Date().toISOString(),
    }
    setDb((d) => ({
      ...d,
      invoices: [
        note,
        ...d.invoices.map((i) => (i.id === invoiceId
          ? { ...i, status: 'cancelled' as const, cancelled_at: new Date().toISOString() }
          : i)),
      ],
      invoice_items: [
        ...d.invoice_items,
        ...items.map((it) => ({
          ...it,
          id: crypto.randomUUID(),
          invoice_id: noteId,
          unit_price: -it.unit_price,
          amount_net: -it.amount_net,
          amount_vat: -it.amount_vat,
          amount_gross: -it.amount_gross,
        })),
      ],
    }))
    return noteId
  }, [live, db.invoices, db.invoice_items, nextReference])

  /**
   * A payment link for a guest, created on the landlord's own Stripe account. The
   * money settles to their balance and their payout schedule — StaySuite only asks
   * their account to open a checkout.
   */
  const guestPaymentLink = useCallback(async (invoiceId: string) => {
    if (!live || !supabase) return null
    try {
      const { data, error } = await supabase.functions.invoke('stripe-connect', {
        body: { action: 'payment_link', invoice_id: invoiceId },
      })
      if (error) throw error
      const url = (data as { url?: string })?.url ?? null
      setError(null)
      return url
    } catch (e) { failed(e); return null }
  }, [live])

  const addPayment = useCallback(async (p: Omit<Payment, 'id' | 'tenant_id' | 'created_at'>) => {
    const payment: Payment = {
      ...p,
      id: crypto.randomUUID(),
      tenant_id: db.tenant.id,
      created_at: new Date().toISOString(),
    }
    // Eine gebuchte Anzahlung stempelt die Buchung ab — dieselbe Spalte, die Stripe
    // setzt, wenn der Gast online bezahlt. Sonst hinge der Zahlungsstand davon ab,
    // WIE bezahlt wurde, und eine Überweisung wäre in der Buchung unsichtbar.
    // Datiert wird auf den Eingangstag, nicht auf jetzt: der Vermieter trägt die
    // Überweisung von Montag oft erst am Donnerstag ein.
    const stampPrepaid = payment.kind === 'prepayment' && Boolean(payment.booking_id)
    const prepaidAt = stampPrepaid
      ? new Date(`${payment.received_on}T12:00:00`).toISOString()
      : null
    const invoice = payment.invoice_id
      ? db.invoices.find((i) => i.id === payment.invoice_id)
      : undefined
    const paidTotal = invoice ? invoice.paid_amount + payment.amount : 0
    const invoicePatch: Partial<Invoice> | null = invoice
      ? {
          paid_amount: paidTotal,
          status: paidTotal + 0.005 >= invoice.total_gross ? 'paid' : 'partly_paid',
          paid_at: paidTotal + 0.005 >= invoice.total_gross
            ? new Date().toISOString() : invoice.paid_at ?? null,
        }
      : null

    setDb((d) => ({
      ...d,
      payments: [payment, ...d.payments],
      invoices: invoicePatch
        ? d.invoices.map((i) => (i.id === payment.invoice_id ? { ...i, ...invoicePatch } : i))
        : d.invoices,
      bookings: payment.booking_id && (payment.kind === 'security_deposit' || stampPrepaid)
        ? d.bookings.map((b) => (b.id === payment.booking_id
            ? {
                ...b,
                ...(payment.kind === 'security_deposit' ? { deposit_status: 'held' as const } : {}),
                ...(stampPrepaid ? { prepaid_at: prepaidAt } : {}),
              }
            : b))
        : d.bookings,
    }))

    if (!live || !supabase) return
    try {
      await api.insertPayment(supabase, payment)
      if (invoicePatch && payment.invoice_id) {
        await api.updateInvoice(supabase, payment.invoice_id, invoicePatch)
      }
      // Der Abstempel der Buchung wurde bisher ohne jede Prüfung abgesetzt: nicht
      // einmal ein echter Fehler wäre aufgefallen. Der `catch` unten lädt neu, also
      // genügt es, hier zu werfen.
      if (payment.booking_id && payment.kind === 'security_deposit') {
        api.assertWrote(
          await supabase.from('bookings').update({ deposit_status: 'held' })
            .eq('id', payment.booking_id).select('id'),
          'Kaution auf der Buchung vermerken',
        )
      }
      if (payment.booking_id && stampPrepaid) {
        api.assertWrote(
          await supabase.from('bookings').update({ prepaid_at: prepaidAt })
            .eq('id', payment.booking_id).select('id'),
          'Anzahlung auf der Buchung vermerken',
        )
      }
      setError(null)
    } catch (e) {
      failed(e)
      setReloadToken((t) => t + 1)
    }
  }, [live, db.tenant.id, db.invoices])

  /**
   * Zahlung zurücknehmen.
   *
   * Eine falsch gebuchte Anzahlung ist ein häufiger Fehler — falscher Betrag, falsche
   * Buchung, zweimal geklickt — und ohne diesen Weg bliebe sie für immer auf der
   * Rechnung stehen. Sie wird gelöscht und nicht storniert, weil eine Zahlung kein
   * Beleg ist: der Beleg ist die Rechnung, und die ist unveränderbar.
   *
   * Zurückgenommen wird alles, was die Buchung ausgelöst hat: der Rechnungsstand und
   * der Anzahlungsstempel — letzterer nur, wenn keine weitere Anzahlung übrig bleibt.
   */
  const removePayment = useCallback(async (paymentId: string) => {
    const payment = db.payments.find((p) => p.id === paymentId)
    if (!payment) return

    const invoice = payment.invoice_id
      ? db.invoices.find((i) => i.id === payment.invoice_id)
      : undefined
    const paidTotal = invoice ? Math.max(0, invoice.paid_amount - payment.amount) : 0
    const invoicePatch: Partial<Invoice> | null = invoice
      ? {
          paid_amount: paidTotal,
          status: paidTotal <= 0 ? 'sent'
            : paidTotal + 0.005 >= invoice.total_gross ? 'paid' : 'partly_paid',
          paid_at: paidTotal + 0.005 >= invoice.total_gross ? invoice.paid_at ?? null : null,
        }
      : null

    const stillPrepaid = payment.kind === 'prepayment' && Boolean(payment.booking_id)
      && db.payments.some((p) => p.id !== paymentId
        && p.booking_id === payment.booking_id && p.kind === 'prepayment')
    const clearPrepaid = payment.kind === 'prepayment' && Boolean(payment.booking_id)
      && !stillPrepaid

    setDb((d) => ({
      ...d,
      payments: d.payments.filter((p) => p.id !== paymentId),
      invoices: invoicePatch
        ? d.invoices.map((i) => (i.id === payment.invoice_id ? { ...i, ...invoicePatch } : i))
        : d.invoices,
      bookings: clearPrepaid
        ? d.bookings.map((b) =>
            b.id === payment.booking_id ? { ...b, prepaid_at: null } : b)
        : d.bookings,
    }))

    if (!live || !supabase) return
    try {
      // `select` statt blossem `delete`: ein von RLS abgewiesenes Delete trifft null
      // Zeilen und meldet keinen Fehler. Ohne die Zeilenzahl wäre die Zahlung nur auf
      // dem Schirm zurückgenommen und stünde nach dem Neuladen wieder da — samt dem
      // Rechnungsstand, den sie gesetzt hat.
      api.assertWrote(
        await supabase.from('payments').delete().eq('id', paymentId).select('id'),
        'Zahlung entfernen',
      )
      if (invoicePatch && payment.invoice_id) {
        await api.updateInvoice(supabase, payment.invoice_id, invoicePatch)
      }
      if (clearPrepaid && payment.booking_id) {
        api.assertWrote(
          await supabase.from('bookings').update({ prepaid_at: null })
            .eq('id', payment.booking_id).select('id'),
          'Anzahlungsvermerk entfernen',
        )
      }
      setError(null)
    } catch (e) {
      failed(e)
      setReloadToken((t) => t + 1)
    }
  }, [live, db.payments, db.invoices])

  const paidFor = useCallback((bookingId: string) =>
    db.payments
      .filter((p) => p.booking_id === bookingId && p.kind !== 'security_deposit'
        && p.kind !== 'security_deposit_refund')
      .reduce((a, p) => a + p.amount, 0),
    [db.payments])

  /*
   * Die Rechnungssperre, einmal für alle Ansichten.
   *
   * Die Regel selbst steht in `lib/bookingLock.ts` — rein und getestet, weil sie
   * Geld betrifft und eine Regel in einer Komponente in diesem Projekt nicht
   * automatisiert prüfbar wäre. Hier hängt sie nur an den Daten, wie `isAvailable`
   * es auch tut. Ansichten fragen den Store, nicht dreimal die Rohdaten.
   *
   * Sie gilt in beiden Betriebsarten: sie steht im Store, nicht im Adapter, damit
   * die Demo nicht erlaubt, was live scheitert.
   */
  const invoiceLockFor = useCallback(
    (bookingId: string) => lockingInvoices(db.invoices, bookingId), [db.invoices])

  const draftInvoicesFor = useCallback(
    (bookingId: string) => draftInvoices(db.invoices, bookingId), [db.invoices])

  const saveTenant = useCallback(async (patch: Partial<Database['tenant']>) => {
    setDb((d) => {
      const tenant = { ...d.tenant, ...patch }
      // Mirror the database trigger locally, so the address on a document is right
      // immediately — and so demo mode, which has no trigger, behaves the same.
      if (patch.street !== undefined || patch.postcode !== undefined || patch.city !== undefined) {
        const town = [tenant.postcode, tenant.city].map((s) => s?.trim()).filter(Boolean).join(' ')
        tenant.address = [tenant.street?.trim(), town].filter(Boolean).join(', ') || undefined
      }
      return { ...d, tenant }
    })
    if (!live || !supabase) return
    try {
      await api.updateTenant(supabase, db.tenant.id, patch)
      setError(null)
    } catch (e) {
      /*
       * Der lokale Zustand wurde oben schon gesetzt — das ist gewollt, damit die
       * Eingabe sofort steht. Scheitert der Schreibvorgang, muss er aber zurück,
       * sonst zeigt die Oberfläche einen Wert an, den die Datenbank nie bekommen
       * hat: eine Fehlermeldung neben dem neuen Wert liest sich wie „gespeichert,
       * aber irgendwas hakt". Neu laden holt den wahren Stand.
       */
      failed(e)
      setReloadToken((t) => t + 1)
    }
  }, [live, db.tenant.id])

  const saveTouristTax = useCallback(async (rule: TouristTaxRule) => {
    setTouristTax(rule)
    if (!live || !supabase) return
    try {
      const saved = await api.upsertTouristTax(supabase, db.tenant.id, rule)
      setTouristTax(saved)
      setError(null)
    } catch (e) { failed(e); setReloadToken((t) => t + 1) }
  }, [live, db.tenant.id])

  const saveRatePeriod = useCallback(async (
    id: string, patch: Partial<Database['rate_periods'][number]>,
  ) => {
    setDb((d) => ({
      ...d,
      rate_periods: d.rate_periods.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }))
    if (!live || !supabase) return
    try {
      await api.updateRatePeriod(supabase, id, patch)
      setError(null)
    } catch (e) {
      // Ohne das Neuladen bliebe der nicht gespeicherte Saisonpreis stehen — und
      // `price()` rechnet aus genau diesem Zustand. Eine Fehlermeldung neben einem
      // Preis, mit dem weiter bepreist wird, ist schlimmer als keine.
      failed(e)
      setReloadToken((t) => t + 1)
    }
  }, [live])

  const upsertRatePeriod = useCallback(async (row: Database['rate_periods'][number]) => {
    const next = { ...row, tenant_id: db.tenant.id || row.tenant_id }
    setDb((d) => ({
      ...d,
      rate_periods: d.rate_periods.some((r) => r.id === next.id)
        ? d.rate_periods.map((r) => (r.id === next.id ? next : r))
        : [...d.rate_periods, next],
    }))
    if (!live || !supabase) return
    try {
      const saved = await api.upsertRatePeriod(supabase, next)
      setDb((d) => ({
        ...d,
        rate_periods: d.rate_periods.map((r) => (r.id === saved.id ? saved : r)),
      }))
      setError(null)
    } catch (e) { failed(e); setReloadToken((t) => t + 1) }
  }, [live, db.tenant.id])

  const deleteRatePeriod = useCallback(async (id: string) => {
    const previous = db.rate_periods
    setDb((d) => ({ ...d, rate_periods: d.rate_periods.filter((r) => r.id !== id) }))
    if (!live || !supabase) return
    try {
      await api.deleteRatePeriod(supabase, id)
    } catch (e) { failed(e); setDb((d) => ({ ...d, rate_periods: previous })) }
  }, [live, db.rate_periods])

  const saveFee = useCallback(async (row: Database['fees'][number]) => {
    const next = { ...row, tenant_id: db.tenant.id || row.tenant_id }
    setDb((d) => ({
      ...d,
      fees: d.fees.some((f) => f.id === next.id)
        ? d.fees.map((f) => (f.id === next.id ? next : f))
        : [...d.fees, next],
    }))
    if (!live || !supabase) return
    try {
      const saved = await api.upsertFee(supabase, next)
      setDb((d) => ({ ...d, fees: d.fees.map((f) => (f.id === saved.id ? saved : f)) }))
      setError(null)
    } catch (e) { failed(e); setReloadToken((t) => t + 1) }
  }, [live, db.tenant.id])

  const deleteFee = useCallback(async (id: string) => {
    const previous = db.fees
    setDb((d) => ({ ...d, fees: d.fees.filter((f) => f.id !== id) }))
    if (!live || !supabase) return
    try {
      await api.deleteFee(supabase, id)
    } catch (e) { failed(e); setDb((d) => ({ ...d, fees: previous })) }
  }, [live, db.fees])

  /** Generic optimistic list write-through; all four collections behave identically. */
  const listSave = useCallback(async <K extends 'units' | 'unit_categories'>(
    key: K,
    row: Database[K][number],
    remote: (r: Database[K][number]) => Promise<Database[K][number]>,
  ) => {
    const next = { ...row, tenant_id: db.tenant.id || row.tenant_id }
    setDb((d) => ({
      ...d,
      [key]: (d[key] as Database[K]).some((x) => x.id === next.id)
        ? (d[key] as Database[K]).map((x) => (x.id === next.id ? next : x))
        : [...(d[key] as Database[K]), next],
    }))
    if (!live || !supabase) return
    try {
      const saved = await remote(next)
      setDb((d) => ({
        ...d,
        [key]: (d[key] as Database[K]).map((x) => (x.id === saved.id ? saved : x)),
      }))
      setError(null)
    } catch (e) { failed(e); setReloadToken((t) => t + 1) }
  }, [live, db.tenant.id])

  const listDelete = useCallback(async <K extends 'units' | 'unit_categories'>(
    key: K, id: string, remote: (id: string) => Promise<void>,
  ) => {
    const previous = db[key]
    setDb((d) => ({ ...d, [key]: (d[key] as Database[K]).filter((x) => x.id !== id) }))
    if (!live || !supabase) return
    try {
      await remote(id)
    } catch (e) { failed(e); setDb((d) => ({ ...d, [key]: previous })) }
  }, [live, db])

  const saveUnit = useCallback((row: Database['units'][number]) =>
    listSave('units', row, (r) => api.upsertUnit(supabase!, r)), [listSave])
  const deleteUnit = useCallback((id: string) =>
    listDelete('units', id, (i) => api.deleteUnit(supabase!, i)), [listDelete])
  const saveUnitCategory = useCallback((row: Database['unit_categories'][number]) =>
    listSave('unit_categories', row, (r) => api.upsertUnitCategory(supabase!, r)), [listSave])
  const deleteUnitCategory = useCallback((id: string) =>
    listDelete('unit_categories', id, (i) => api.deleteUnitCategory(supabase!, i)), [listDelete])

  const reload = useCallback(() => setReloadToken((t) => t + 1), [])
  const reset = useCallback(() => {
    localStorage.removeItem(DEMO_STORAGE_KEY)
    setDb(buildDemoDatabase())
  }, [])

  const value = useMemo<StoreValue>(() => ({
    db, live, loading, error, clearError: () => setError(null), touristTax, subscription,
    unitById, guestById, guestName, bookingsInRange, isAvailable, conflictsFor,
    price, saveBooking, deleteBooking, saveGuest, createQuote, createInvoice,
    overlappingBookings,
    issueInvoice, cancelInvoice, guestPaymentLink, addPayment, removePayment, saveTenant, saveTouristTax, saveRatePeriod, upsertRatePeriod,
    deleteRatePeriod, saveFee, deleteFee, saveUnit, deleteUnit,
    saveUnitCategory, deleteUnitCategory, paidFor, invoiceLockFor, draftInvoicesFor,
    reload, reset, nextReference,
  }), [
    db, live, loading, error, touristTax, subscription,
    unitById, guestById, guestName, bookingsInRange, isAvailable, conflictsFor,
    price, saveBooking, deleteBooking, saveGuest, createQuote, createInvoice,
    overlappingBookings,
    issueInvoice, cancelInvoice, guestPaymentLink, addPayment, removePayment, saveTenant, saveTouristTax, saveRatePeriod, upsertRatePeriod,
    deleteRatePeriod, saveFee, deleteFee, saveUnit, deleteUnit,
    saveUnitCategory, deleteUnitCategory, paidFor, invoiceLockFor, draftInvoicesFor,
    reload, reset, nextReference,
  ])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useStore(): StoreValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useStore must be used inside <StoreProvider>')
  return ctx
}
